import React, { useState, useEffect } from "react";
import { REELS } from "../mock/mock";
import { listReels, getInstagramFeed, mediaSrc } from "../lib/api";
import { Play, Instagram, Plus, X } from "lucide-react";
import { BRAND } from "../mock/mock";

const MAX = 5; // curated, elegant count in the hero

function ReelCard({ reel, tier }) {
  const isVideo = reel.type === "video" && reel.src;
  const src = mediaSrc(reel.src || reel.poster);
  const sizeCls =
    tier === "center"
      ? "w-[180px] md:w-[240px] md:-translate-y-3 z-20"
      : tier === "near"
      ? "w-[160px] md:w-[210px] md:-translate-y-1 z-10 md:opacity-95"
      : "w-[150px] md:w-[185px] md:opacity-80";
  return (
    <a
      href={reel.permalink || BRAND.instagram}
      target="_blank"
      rel="noreferrer"
      className={`relative shrink-0 snap-center rounded-[26px] overflow-hidden group border hairline shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:opacity-100 ${sizeCls}`}
    >
      <div className="aspect-[9/16] overflow-hidden bg-black">
        {isVideo ? (
          <video src={src} autoPlay muted loop playsInline poster={reel.poster ? mediaSrc(reel.poster) : undefined} className="w-full h-full object-cover" />
        ) : (
          <img src={src} alt={reel.caption || "VIIMII reel"} className="w-full h-full object-cover kenburns" loading="lazy" />
        )}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/10" />
      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 backdrop-blur-md grid place-items-center border border-white/25">
        <Play size={13} className="text-white fill-white ml-0.5" />
      </div>
      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <Instagram size={12} />
        <span className="text-[11px] font-medium truncate">{reel.caption || "@_.viimii._"}</span>
      </div>
    </a>
  );
}

function MoreCard({ count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative shrink-0 snap-center w-[150px] md:w-[185px] rounded-[26px] overflow-hidden border hairline shadow-2xl md:opacity-90 hover:opacity-100 transition-all duration-500 hover:-translate-y-2 bg-viimii-2"
    >
      <div className="aspect-[9/16] flex flex-col items-center justify-center gap-3 px-4 text-center">
        <span className="w-12 h-12 rounded-full grid place-items-center text-black" style={{ background: "var(--viimii-gold)" }}>
          <Plus size={22} />
        </span>
        <span className="font-display text-lg text-cream leading-tight">View More<br />Reels</span>
        {count > 0 && <span className="text-xs text-cream/55">+{count} more</span>}
      </div>
    </button>
  );
}

export default function ReelsRow() {
  const [reels, setReels] = useState(REELS.slice(0, MAX));
  const [all, setAll] = useState(REELS);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let done = false;
    getInstagramFeed(48)
      .then((res) => {
        const data = (res && res.data) || [];
        if (res && res.configured && data.length) {
          const map = (m) =>
            m.media_type === "VIDEO"
              ? { id: m.id, type: "video", src: m.image_url, poster: m.thumbnail_url, caption: (m.caption || "").slice(0, 60), permalink: m.permalink }
              : { id: m.id, type: "image", poster: m.image_url, caption: (m.caption || "").slice(0, 60), permalink: m.permalink };
          const videos = data.filter((m) => m.media_type === "VIDEO");
          const fullList = (videos.length ? videos : data).map(map);
          if (fullList.length) {
            setAll(fullList);
            setReels(fullList.slice(0, MAX));
            done = true;
          }
        }
      })
      .catch((e) => { if (process.env.NODE_ENV !== "production") console.error("IG reels failed:", e); })
      .finally(() => {
        if (!done) {
          listReels()
            .then((d) => { if (!done && d && d.length) { setAll(d); setReels(d.slice(0, MAX)); } })
            .catch(() => {});
        }
      });
  }, []);

  const center = Math.floor((reels.length - 1) / 2);
  const tierOf = (i) => {
    const d = Math.abs(i - center);
    return d === 0 ? "center" : d === 1 ? "near" : "far";
  };
  const extra = Math.max(0, all.length - reels.length);

  return (
    <>
      <div className="flex items-center md:justify-center gap-3 md:gap-4 overflow-x-auto md:overflow-visible px-4 pb-3 snap-x scrollbar-hide">
        {reels.map((r, i) => (
          <ReelCard key={`${r.id}-${i}`} reel={r} tier={tierOf(i)} />
        ))}
        <MoreCard count={extra} onClick={() => setOpen(true)} />
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] flex flex-col" role="dialog">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative flex-1 overflow-y-auto py-8 px-5 md:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-6 sticky top-0">
                <div className="flex items-center gap-2 text-cream">
                  <Instagram size={18} className="text-gold" style={{ color: "var(--viimii-gold-soft)" }} />
                  <span className="font-display text-2xl text-white">VIIMII Reels</span>
                </div>
                <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-full grid place-items-center bg-white/15 text-white hover:bg-white/25 transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {all.map((r, i) => (
                  <a
                    key={`${r.id}-all-${i}`}
                    href={r.permalink || BRAND.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="relative rounded-2xl overflow-hidden group border border-white/10 bg-black"
                  >
                    <div className="aspect-[9/16] overflow-hidden">
                      <img src={mediaSrc(r.poster || r.src)} alt={r.caption || "VIIMII reel"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <span className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/15 backdrop-blur-md grid place-items-center border border-white/25">
                      <Play size={12} className="text-white fill-white ml-0.5" />
                    </span>
                    {r.caption && <span className="absolute bottom-2.5 left-2.5 right-2.5 text-[11px] text-white/90 truncate">{r.caption}</span>}
                  </a>
                ))}
              </div>
              <div className="text-center mt-8">
                <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-black font-medium" style={{ background: "var(--viimii-gold)" }}>
                  <Instagram size={16} /> See all on Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
